import { canSeeAdminOnlyContent } from "../projects-helpers.js";
function visibleGlobalDocuments(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx))
        return projectZql.globalDocuments;
    return projectZql.globalDocuments.where("bucket", "team");
}
function visibleGlobalDocumentLinks(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx))
        return projectZql.globalDocumentLinks;
    return projectZql.globalDocumentLinks.whereExists("globalDocument", (doc) => doc.where("bucket", "team"));
}
function assertBucketAccess(ctx, bucket) {
    if (bucket === "managers" && !canSeeAdminOnlyContent(ctx)) {
        throw new Error("Not allowed to access the managers bucket");
    }
}
/** Every folder/document below `rootID` in the Docs tree. */
async function globalDocumentDescendants(tx, projectZql, rootID) {
    const rows = (await tx.run(projectZql.globalDocuments));
    const childrenByParent = new Map();
    for (const row of rows) {
        if (!row.parentID)
            continue;
        const children = childrenByParent.get(row.parentID) ?? [];
        children.push(row);
        childrenByParent.set(row.parentID, children);
    }
    const result = [];
    const queue = [...(childrenByParent.get(rootID) ?? [])];
    const seen = new Set([rootID]);
    while (queue.length > 0) {
        const row = queue.shift();
        if (seen.has(row.id))
            continue;
        seen.add(row.id);
        result.push(row);
        queue.push(...(childrenByParent.get(row.id) ?? []));
    }
    return result;
}
export function createDocumentQueries({ defineProjectQuery, projectZql, }) {
    if (!defineProjectQuery) {
        throw new Error("Missing document query dependencies");
    }
    return {
        studioDocuments: {
            all: defineProjectQuery(({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studioDocuments.where("id", "__unauthorized__");
                }
                return projectZql.studioDocuments
                    .related("document")
                    .related("studio")
                    .orderBy("title", "asc");
            }),
            byStudio: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.studioDocuments.where("id", "__unauthorized__");
                }
                return projectZql.studioDocuments
                    .where("studioID", args.studioID)
                    .related("document")
                    .related("studio")
                    .orderBy("title", "asc");
            }),
        },
        globalDocuments: {
            all: defineProjectQuery(({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocuments.where("id", "__unauthorized__");
                }
                return visibleGlobalDocuments(ctx, projectZql)
                    .related("document")
                    .related("links")
                    .orderBy("sortOrder", "asc")
                    .orderBy("title", "asc");
            }),
            list: defineProjectQuery(({ ctx }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocuments.where("id", "__unauthorized__");
                }
                return visibleGlobalDocuments(ctx, projectZql)
                    .where("hiddenFromDocs", false)
                    .related("document")
                    .related("links")
                    .orderBy("sortOrder", "asc")
                    .orderBy("title", "asc");
            }),
        },
        globalDocumentLinks: {
            byEntity: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocumentLinks.where("id", "__unauthorized__");
                }
                return visibleGlobalDocumentLinks(ctx, projectZql)
                    .where("linkType", args.linkType)
                    .where("linkID", args.linkID)
                    .orderBy("createdAt", "asc");
            }),
            byDocument: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.globalDocumentLinks.where("id", "__unauthorized__");
                }
                return visibleGlobalDocumentLinks(ctx, projectZql)
                    .where("globalDocumentID", args.documentID)
                    .orderBy("createdAt", "asc");
            }),
        },
    };
}
export function createDocumentMutators({ defineProjectMutator, projectZql, schemas, assertAdminDataWriteAccess, }) {
    if (!defineProjectMutator || !schemas || !assertAdminDataWriteAccess) {
        throw new Error("Missing document mutator dependencies");
    }
    return {
        studioDocuments: {
            create: defineProjectMutator(schemas.createStudioDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.documents.insert({
                    id: args.documentID,
                    createdAt: now,
                    updatedAt: now,
                });
                await tx.mutate.studioDocuments.insert({
                    id: args.id,
                    title: args.title,
                    studioID: args.studioID,
                    documentID: args.documentID,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(schemas.updateStudioDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.studioDocuments.update({
                    id: args.id,
                    title: args.title,
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.studioDocumentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const doc = await tx.run(projectZql.studioDocuments.where("id", args.id).one());
                await tx.mutate.studioDocuments.delete({ id: args.id });
                if (doc?.documentID) {
                    await tx.mutate.documents.delete({ id: doc.documentID });
                }
            }),
        },
        globalDocuments: {
            create: defineProjectMutator(schemas.createGlobalDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const parent = args.parentID
                    ? await tx.run(projectZql.globalDocuments.where("id", args.parentID).one())
                    : null;
                // Items are created in their folder's bucket.
                const bucket = parent?.bucket ?? args.bucket ?? "team";
                if (args.bucket && args.bucket !== bucket) {
                    throw new Error("Documents must be in the same bucket as their folder");
                }
                if (args.hiddenFromDocs && bucket !== "team") {
                    throw new Error("Linked documents must stay in the team bucket");
                }
                assertBucketAccess(ctx, bucket);
                const now = Date.now();
                const documentID = args.isFolder ? null : args.documentID;
                if (documentID) {
                    await tx.mutate.documents.insert({
                        id: documentID,
                        createdAt: now,
                        updatedAt: now,
                    });
                }
                await tx.mutate.globalDocuments.insert({
                    id: args.id,
                    title: args.title,
                    parentID: args.parentID ?? null,
                    isFolder: args.isFolder ?? false,
                    hiddenFromDocs: args.hiddenFromDocs ?? false,
                    bucket,
                    documentID,
                    sortOrder: 0,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(schemas.updateGlobalDocumentInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const existing = await tx.run(projectZql.globalDocuments.where("id", args.id).one());
                assertBucketAccess(ctx, existing?.bucket);
                const parent = args.parentID
                    ? await tx.run(projectZql.globalDocuments.where("id", args.parentID).one())
                    : null;
                const currentBucket = existing?.bucket ?? "team";
                const bucket = parent?.bucket ?? args.bucket ?? currentBucket;
                if (args.bucket && args.bucket !== bucket) {
                    throw new Error("Documents must be in the same bucket as their folder");
                }
                assertBucketAccess(ctx, bucket);
                const updates = { ...args, bucket };
                const movesBucket = bucket !== currentBucket;
                const descendants = movesBucket
                    ? await globalDocumentDescendants(tx, projectZql, args.id)
                    : [];
                if (movesBucket) {
                    // Linked docs (project notes, SIM docs) must never disappear
                    // from employees, also not as part of a moved folder.
                    if (existing?.hiddenFromDocs ||
                        descendants.some((row) => row.hiddenFromDocs)) {
                        throw new Error("Linked documents must stay in the team bucket");
                    }
                    // Moving to the other bucket without a target folder lands at
                    // its root: the old parent belongs to the old bucket.
                    if (args.parentID === undefined)
                        updates.parentID = null;
                }
                const now = Date.now();
                await tx.mutate.globalDocuments.update({
                    ...updates,
                    updatedAt: now,
                });
                // A folder moves to another bucket together with its contents.
                for (const row of descendants) {
                    await tx.mutate.globalDocuments.update({
                        id: row.id,
                        bucket,
                        updatedAt: now,
                    });
                }
            }),
            delete: defineProjectMutator(schemas.globalDocumentIDInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const doc = await tx.run(projectZql.globalDocuments.where("id", args.id).one());
                assertBucketAccess(ctx, doc?.bucket);
                await tx.mutate.globalDocuments.delete({ id: args.id });
                if (doc?.documentID) {
                    await tx.mutate.documents.delete({ id: doc.documentID });
                }
            }),
        },
        globalDocumentLinks: {
            create: defineProjectMutator(schemas.createGlobalDocumentLinkInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                const doc = await tx.run(projectZql.globalDocuments.where("id", args.globalDocumentID).one());
                assertBucketAccess(ctx, doc?.bucket);
                const existing = await tx.run(projectZql.globalDocumentLinks
                    .where("globalDocumentID", args.globalDocumentID)
                    .where("linkType", args.linkType)
                    .where("linkID", args.linkID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.globalDocumentLinks.insert({
                    ...args,
                    createdAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(schemas.deleteGlobalDocumentLinkInputSchema, async ({ tx, ctx, args, }) => {
                assertAdminDataWriteAccess(ctx);
                await tx.mutate.globalDocumentLinks.delete({ id: args.id });
            }),
        },
    };
}
